import {ItemQuantity} from "../State/Inventory";

/**
 * Smelter unlocking game logic and utilities
 *
 * This module contains game mechanics, difficulty calculations,
 * and state management for the smelter unlocking mini-games.
 */

/** Resource types that use specific minigames */
export const TEMPERATURE_RESOURCES = ["iron", "copper"];
export const POURING_RESOURCES = ["steel"];
export const INSPECTION_RESOURCES = ["stone"];

/** Props for the main SmeltingUnlockModal component */
export type SmeltingUnlockModalProps = {
    resourceType: string;
    clearedSpots: number;
    isOpen: boolean;
    onClose: () => void;
    onComplete: (rewards: ItemQuantity[]) => void;
};

/** State for the temperature control game */
export type TemperatureGameState = {
    temperature: number; // 0-100
    airflow: number; // 0-100
    inGreenZone: boolean;
    timeInZone: number; // milliseconds
    gameStarted: boolean;
    success: boolean;
    failure: boolean;
};

/** Props for the TemperatureControlGame component */
export type TemperatureControlGameProps = {
    resourceType: string;
    clearedSpots: number;
    onComplete: (rewards: ItemQuantity[]) => void;
};

/** State for an individual mold in the pouring game */
export type MoldState = {
    id: number;
    fullness: number; // 0-100
    targetFullness: number; // target value
    pouring: boolean;
    complete: boolean;
    failed: boolean;
};

/** Props for the MetalPouringGame component */
export type MetalPouringGameProps = {
    resourceType: string;
    clearedSpots: number;
    onComplete: (rewards: ItemQuantity[]) => void;
};

/** State for an individual brick in the inspection game */
export type BrickState = {
    id: number;
    row: number;
    col: number;
    isBroken: boolean;
    isClicked: boolean;
};

/** Props for the BrickInspectionGame component */
export type BrickInspectionGameProps = {
    resourceType: string;
    clearedSpots: number;
    onComplete: (rewards: ItemQuantity[]) => void;
};

/**
 * Temperature Control Game Difficulty Settings
 */
export const getTemperatureSettings = (clearedSpots: number) => {
    const difficulty = clearedSpots;

    // Green zone size (percentage of gauge)
    const greenZoneSize = Math.max(3, 33 - difficulty * 3);

    // Green zone center (around 60-70% of gauge)
    const greenZoneCenter = 65;
    const greenZoneMin = greenZoneCenter - greenZoneSize / 2;
    const greenZoneMax = greenZoneCenter + greenZoneSize / 2;

    // Wheel spins required to fill airflow
    const wheelSpinsRequired = 3 + difficulty * 2.7;

    // Time to maintain temperature (seconds)
    const maintainTime = 5;

    // Penalty for leaving zone (seconds before failure)
    const penaltyTime = clearedSpots === 0 ? Infinity : Math.max(1, 10 - difficulty);

    return {
        greenZoneMin,
        greenZoneMax,
        greenZoneCenter,
        greenZoneSize,
        wheelSpinsRequired,
        maintainTime,
        penaltyTime,
    };
};

/**
 * Metal Pouring Game Difficulty Settings
 */
export const getPouringSettings = (clearedSpots: number) => {
    const difficulty = clearedSpots;

    // Number of molds to fill correctly
    const moldsRequired = 3 + difficulty * 0.7;

    // Fill speed (percentage per 100ms)
    const fillSpeed = 1 + difficulty * 0.3;

    // Tolerance for "correct" fullness (percentage)
    const tolerance = Math.max(5, 10 - difficulty * 0.5);

    // Target fullness (around 80%)
    const targetFullness = 80;

    return {
        moldsRequired: Math.floor(moldsRequired),
        fillSpeed,
        tolerance,
        targetFullness,
    };
};

/**
 * Brick Inspection Game Difficulty Settings
 */
export const getInspectionSettings = (clearedSpots: number) => {
    const difficulty = clearedSpots;

    // Grid size (rows and cols)
    const gridSize = Math.floor(3 + difficulty * 1.7);

    // Number of broken bricks
    const brokenCount = Math.max(1, 1 + Math.floor(difficulty * 0.5));

    return {
        gridSize: Math.min(gridSize, 20),
        brokenCount: Math.min(brokenCount, Math.floor(gridSize * gridSize / 2)),
    };
};

/**
 * Generate the localStorage key for saving progress on a specific smelter
 *
 * @param resourceType - Type of resource being smelted
 * @returns localStorage key string
 */
export const getSmelterProgressKey = (resourceType: string): string => {
    return `smelter-progress-${resourceType}`;
};
